import gamanzaTournamentOrderInterceptor from './gamanzaTournamentOrderInterceptor';

class GamanzaWidgetService {
  constructor() {
    this.isInitialized = false;
    this.initializing = false;
    this.loadingScript = null;
    this.currentKey = '';
    this.initializationPromise = null;
    this.initId = 0;
    this.accessToken = '';
    this.serviceUrl = '';
  }

  loadScript(bundleUrl) {
    if (!bundleUrl) {
      return Promise.reject(new Error('Gamanza bundleUrl is required'));
    }

    if (window.GamanzaEngageClient) {
      return Promise.resolve();
    }

    if (this.loadingScript) {
      return this.loadingScript;
    }

    this.loadingScript = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${bundleUrl}"]`);

      if (existingScript) {
        existingScript.addEventListener('load', resolve, { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = bundleUrl;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error('GamanzaEngageClient script failed to load'));

      document.head.appendChild(script);
    });

    return this.loadingScript;
  }

  async init({ gamanzaConfig, identityToken }) {
    if (!gamanzaConfig?.enabled || !identityToken) {
      return;
    }

    if (this.initializing) {
      return this.initializationPromise;
    }

    const serviceUrl = gamanzaConfig.serviceUrl;
    const widgetConfig = { ...gamanzaConfig, serviceUrl };
    const initKey = [serviceUrl, widgetConfig.clientId, identityToken, widgetConfig.playerLocale || 'es'].join('|');

    if (this.isInitialized && this.currentKey === initKey) {
      return this.accessToken;
    }

    if (this.isInitialized) {
      this.destroy();
    }

    const config = {
      init: {
        serviceUrl: widgetConfig.serviceUrl,
        clientId: widgetConfig.clientId,
        playerIdentityToken: identityToken,
        playerLocale: widgetConfig.playerLocale || 'es'
      },
      lookAndFeel: widgetConfig.lookAndFeel || {}
    };

    const initId = ++this.initId;
    this.initializing = true;
    this.initializationPromise = (async () => {
      gamanzaTournamentOrderInterceptor.install(widgetConfig.serviceUrl);
      await this.loadScript(widgetConfig.bundleUrl);

      const gmg = window.GamanzaEngageClient;

      if (!gmg) {
        throw new Error('GamanzaEngageClient is not loaded');
      }

      const accessToken = await this.validateIdentityToken(widgetConfig, identityToken);

      await new Promise((resolve, reject) => {
        try {
          gmg.init(config, resolve);
        } catch (error) {
          reject(error);
        }
      });

      if (initId !== this.initId) {
        throw new Error('Gamanza initialization cancelled');
      }

      this.isInitialized = true;
      this.currentKey = initKey;
      this.accessToken = accessToken;
      this.serviceUrl = serviceUrl;
      return accessToken;
    })();

    try {
      return await this.initializationPromise;
    } catch (error) {
      if (initId === this.initId) {
        this.destroy();
      }
      throw error;
    } finally {
      if (initId === this.initId) {
        this.initializing = false;
        this.initializationPromise = null;
      }
    }
  }

  async validateIdentityToken(gamanzaConfig, identityToken, attempt = 1, maxAttempts = 4) {
    const url = `${gamanzaConfig.serviceUrl.replace(/\/+$/, '')}/api/oauth/v1/token`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: gamanzaConfig.clientId,
          identityToken
        })
      });

      if (response.ok) {
        const { accessToken } = await response.json();
        if (accessToken) return accessToken;
        throw new Error('Gamanza token validation returned no access token');
      }

      if ((response.status === 401 || response.status >= 500) && attempt < maxAttempts) {
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 8000);
        console.warn(`Gamanza token validation failed (${response.status}), retrying in ${delay}ms (attempt ${attempt}/${maxAttempts})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.validateIdentityToken(gamanzaConfig, identityToken, attempt + 1, maxAttempts);
      }

      throw new Error(`Gamanza token validation failed: ${response.status} ${response.statusText}`);
    } catch (error) {
      if (attempt < maxAttempts && error.name === 'TypeError') {
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 8000);
        console.warn(`Gamanza token validation network error, retrying in ${delay}ms (attempt ${attempt}/${maxAttempts})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.validateIdentityToken(gamanzaConfig, identityToken, attempt + 1, maxAttempts);
      }
      throw error;
    }
  }

  reload() {
    if (!this.isInitialized || !window.GamanzaEngageClient) {
      return;
    }

    window.GamanzaEngageClient.getInstance((GamificationWidgets) => {
      if (GamificationWidgets) {
        GamificationWidgets.reload();
      }
    });
  }

  async getPlayerActiveTournaments() {
    if (!this.accessToken || !this.serviceUrl) {
      return [];
    }

    // Reuse the validation token to avoid a second Gamanza client session.
    const response = await fetch(
      `${this.serviceUrl.replace(/\/+$/, '')}/api/tournaments/v1/ui/filter-eligible-tournaments-for-player`,
      {
        headers: {
          Authorization: `Bearer ${this.accessToken}`
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Gamanza active tournaments request failed: ${response.status}`);
    }

    const data = await response.json();
    return Array.isArray(data?.tournaments) ? data.tournaments : [];
  }

  reloadOne(widgetId) {
    if (!this.isInitialized || !window.GamanzaEngageClient || !widgetId) {
      return;
    }

    window.GamanzaEngageClient.getInstance((GamificationWidgets) => {
      if (GamificationWidgets?.reloadOne) {
        GamificationWidgets.reloadOne(widgetId);
      }
    });
  }

  destroy() {
    this.initId++;

    gamanzaTournamentOrderInterceptor.destroy();

    if (window.GamanzaEngageClient) {
      window.GamanzaEngageClient.getInstance((GamificationWidgets) => {
        if (GamificationWidgets) {
          GamificationWidgets.destroyWidgets();
        }
      });
    }

    this.isInitialized = false;
    this.currentKey = '';
    this.initializing = false;
    this.initializationPromise = null;
    this.accessToken = '';
    this.serviceUrl = '';
  }

  getIsInitialized() {
    return this.isInitialized;
  }
}

export default new GamanzaWidgetService();
